/**
 * VM Control Actions
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { getVmById } from '../globals.js';
import { getCurrentUser } from '../app/init.js';
import {
  declareLoading,
  declareLoaded,
  notificationDialog,
  confirmDialog,
} from '../ui/dialogs.js';
import { escapeHtml } from '../utils.js';

/**
 * Start a VM
 */
export async function vmStart(id: number): Promise<void> {
  declareLoading();
  await apiCall(api.startVm(id), 'VM Start Failed');
  declareLoaded();
}

/**
 * Stop a VM gracefully
 */
export async function vmStop(id: number): Promise<void> {
  confirmDialog(
    'Are you sure you want to stop this VM? Data could be lost if people are on it. Please wait 15 seconds for VM to shutdown cleanly',
    async () => {
      declareLoading();
      const stopped = await apiCall(api.stopVm(id, 15), 'VM Stop Failed', () => {
        notificationDialog(
          'The VM did not shutdown. It may be doing updates, check using "Console". You may also try "Force Off" as a last resort'
        );
      });
      declareLoaded();
      if (stopped) {
        notificationDialog('The VM successfully shutdown cleanly');
      }
    }
  );
}

/**
 * Restart a VM
 */
export async function vmRestart(id: number): Promise<void> {
  confirmDialog(
    'Are you sure you want to restart this VM? Data could be lost if people are on it. Please wait 15 seconds for VM to shutdown cleanly for restart',
    async () => {
      declareLoading();
      const stopped = await apiCall(api.stopVm(id, 15), 'VM Restart Failed');
      if (stopped) await apiCall(api.startVm(id), 'VM Restart Failed');
      declareLoaded();
    }
  );
}

/**
 * Force restart a VM (kill + start)
 */
export async function vmForceRestart(id: number): Promise<void> {
  confirmDialog(
    'Are you sure you want to force restart? This immediately kills the VM (like pulling the power cord) and restarts it. Do this only if the VM is stuck.',
    async () => {
      declareLoading();
      const killed = await apiCall(api.killVm(id), 'Force Restart Failed');
      if (killed) await apiCall(api.startVm(id), 'Force Restart Failed');
      declareLoaded();
    }
  );
}

/**
 * Force power off a VM
 */
export async function vmForcePowerOff(id: number): Promise<void> {
  confirmDialog(
    'Are you sure you want to force power off? Do this only if the VM is stuck',
    async () => {
      declareLoading();
      await apiCall(api.killVm(id), 'Force Power Off Failed');
      declareLoaded();
    }
  );
}

/**
 * Pause a VM
 */
export async function vmPause(id: number): Promise<void> {
  declareLoading();
  await apiCall(api.pauseVm(id), 'Pause Failed');
  declareLoaded();
}

/**
 * Resume a paused VM
 */
export async function vmResume(id: number): Promise<void> {
  declareLoading();
  await apiCall(api.resumeVm(id), 'Resume Failed');
  declareLoaded();
}

/**
 * Delete a VM
 */
export async function vmDelete(id: number): Promise<void> {
  const vm = getVmById(id);
  if (!vm) return;

  if (vm.status === 'running') {
    notificationDialog('Cannot delete a running VM. Stop it first.');
    return;
  }

  let msg = `Are you sure you want to delete <strong>${escapeHtml(vm.name)}</strong>?`;

  // Warn if this VM is owned by a remote peer
  if (vm.owner_host) {
    msg += `<p style="color:#b94a48;margin-top:10px;">This VM is owned by peer <strong>${escapeHtml(vm.owner_host)}</strong>. ` +
           `Deleting it locally may cause the peer to re-create it.</p>`;
  }

  // Warn if this VM is delegated to a remote host
  if (vm.host) {
    msg += `<p style="color:#b94a48;margin-top:10px;">This VM is delegated to host <strong>${escapeHtml(vm.host)}</strong>. ` +
           `The remote copy will also be deleted.</p>`;
  }

  // Show preserved disk volumes
  if (vm.disks && vm.disks.length > 0) {
    msg += `<p style="margin-top:10px;">The following disk volumes will <strong>not</strong> be deleted:</p><ul>`;
    for (const disk of vm.disks) {
      msg += `<li><code>${escapeHtml(disk.path)}</code></li>`;
    }
    msg += `</ul>`;
  }

  msg += `<p style="margin-top:10px;">This action removes only the VM configuration.</p>`;

  confirmDialog(msg, async () => {
    declareLoading();
    await apiCall(api.deleteVm(id), 'Delete Failed');
    declareLoaded();
  }, undefined, true);
}

/**
 * Open VNC console for a VM
 */
export async function vmConnectVnc(id: number): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  declareLoading();
  const vncInfo = await apiCall(api.getVmVnc(id), 'VNC Connection Failed');
  declareLoaded();
  if (!vncInfo) return;

  // Import and use VNC console module
  // Pass the VM's host for fetching resources from the correct delegated host
  const { openVncConsole } = await import('../vnc/console.js');
  const vmData = vm as unknown as Record<string, unknown>;
  const vmHost = vmData.host as string | undefined;
  openVncConsole(vm.id, vm.name, vncInfo, vmHost);
}

/**
 * Open guest VNC console for a VM (connects to VNC server inside the guest)
 */
export async function vmConnectGuestVnc(id: number): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  declareLoading();
  const info = await apiCall(api.getVmGuestVnc(id), 'Guest VNC Connection Failed');
  declareLoaded();
  if (!info) return;

  if (!info.available) {
    notificationDialog('Guest VNC not available: guest IP could not be resolved or VNC port (5900) is not reachable');
    return;
  }

  const { openVncConsole } = await import('../vnc/console.js');
  const vmData = vm as unknown as Record<string, unknown>;
  const vmHost = vmData.host as string | undefined;
  openVncConsole(vm.id, vm.name, { host: info.guest_ip, port: info.port },
                 vmHost, `/ws/vm/${vm.id}/guest-vnc`, true);
}

/**
 * Connect to a VM via RDP (download .rdp file or show connection info)
 */
export async function vmConnectRdp(id: number): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  declareLoading();
  const rdpInfo = await apiCall(api.connectVmRdp(id), 'RDP Connection Failed');
  declareLoaded();
  if (!rdpInfo) return;

  const user = getCurrentUser();
  const downloadRdp = user?.settings?.download_rdp !== false;

  if (downloadRdp) {
    // Set a short-lived auth cookie for the download request
    const token = api.getToken();
    if (token) {
      document.cookie = `vmsman_download_token=${encodeURIComponent(token)}; path=/api/vm/${id}/rdp/download; max-age=30; SameSite=Strict`;
    }

    const a = document.createElement('a');
    a.href = api.getVmRdpDownloadUrl(id);
    a.download = `${vm.name}.rdp`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else {
    const rdpUri = `rdp://full%20address=s:${encodeURIComponent(rdpInfo.host)}:${rdpInfo.port}`;
    const content = `
      <div>
        <p>RDP connection ready for <strong>${escapeHtml(vm.name)}</strong>.</p>
        <table class="connection-info" style="margin: 10px 0;">
          <tr><td><strong>Host:</strong></td><td>${escapeHtml(rdpInfo.host)}</td></tr>
          <tr><td><strong>Port:</strong></td><td>${rdpInfo.port}</td></tr>
        </table>
        <p><a href="${escapeHtml(rdpUri)}">Open in RDP Client</a></p>
        <p style="font-size: 0.85em; color: var(--color-text-muted);">
          Or connect manually using your RDP client with the address above.
        </p>
      </div>
    `;

    $('<div></div>')
      .appendTo('body')
      .html(content)
      .dialog({
        modal: true,
        title: 'RDP Connection',
        zIndex: 10000,
        autoOpen: true,
        width: 400,
        resizable: false,
        buttons: {
          'Download .rdp': function () {
            const token = api.getToken();
            if (token) {
              document.cookie = `vmsman_download_token=${encodeURIComponent(token)}; path=/api/vm/${id}/rdp/download; max-age=30; SameSite=Strict`;
            }
            const a = document.createElement('a');
            a.href = api.getVmRdpDownloadUrl(id);
            a.download = `${vm.name}.rdp`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          },
          Close: function () {
            $(this).dialog('close');
          },
        },
        close: function () {
          $(this).remove();
        },
      });
  }
}

/**
 * Open serial console for a VM
 */
export async function vmOpenSerial(id: number): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  const { openSerialConsole } = await import('../serial/console.js');
  openSerialConsole(vm.id, vm.name);
}

/**
 * Open QEMU monitor for a VM
 */
export async function vmOpenMonitor(id: number): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  // Import and use monitor module
  const { openMonitorConsole } = await import('../monitor/terminal.js');
  openMonitorConsole(vm.id, vm.name);
}

// Export functions to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).vmStart = vmStart;
  (window as unknown as Record<string, unknown>).vmStop = vmStop;
  (window as unknown as Record<string, unknown>).vmRestart = vmRestart;
  (window as unknown as Record<string, unknown>).vmForceRestart = vmForceRestart;
  (window as unknown as Record<string, unknown>).vmForcePowerOff = vmForcePowerOff;
  (window as unknown as Record<string, unknown>).vmPause = vmPause;
  (window as unknown as Record<string, unknown>).vmResume = vmResume;
  (window as unknown as Record<string, unknown>).vmDelete = vmDelete;
  (window as unknown as Record<string, unknown>).vmConnectVnc = vmConnectVnc;
  (window as unknown as Record<string, unknown>).vmConnectGuestVnc = vmConnectGuestVnc;
  (window as unknown as Record<string, unknown>).vmConnectRdp = vmConnectRdp;
  (window as unknown as Record<string, unknown>).vmOpenMonitor = vmOpenMonitor;
  (window as unknown as Record<string, unknown>).vmOpenSerial = vmOpenSerial;
}
